import type { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import Icon from '@components/common/Icon';
import PageHeader from '@components/common/PageHeader';
import Input from '@/components/Form/Input/Input';
import Select from '@components/Form/Select/Select';
import Modal from '@components/common/Modal/Modal';
import ConfirmModal from '@components/common/ConfirmModal/ConfirmModal';
import Button from '@components/Button/Button';
import {
    TableRowActionsTooltipProvider,
    TableRowIconActions,
} from '@components/common/TableRowIconActions';
import applicationsService from '@/services/applications/applicationsService';
import cohortsService from '@/services/cohorts/cohortsService';
import mentorsService from '@/services/mentors/mentorsService';

const getFileUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace('/api/v1', '');
    return `${baseUrl}/${path.startsWith('/') ? path.slice(1) : path}`;
};

const StudentApplicationsPage = () => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [cohortFilter, setCohortFilter] = useState('');
    const [assignmentFilter, setAssignmentFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const PAGE_SIZE = 20;

    const [selectedApp, setSelectedApp] = useState<any | null>(null);
    const [selectedAppIndex, setSelectedAppIndex] = useState<number>(-1);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [selectedMentorId, setSelectedMentorId] = useState('');

    const deleteMutation = useMutation({
        mutationFn: (id: string) => applicationsService.deleteApplication(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
            toast.success('Application deleted successfully');
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message ?? 'Failed to delete application');
        },
    });

    const [exportJobId, setExportJobId] = useState<string | null>(null);
    const [exportStatus, setExportStatus] = useState<string>('idle');
    const [exportProgress, setExportProgress] = useState<number>(0);

    const startExportMutation = useMutation({
        mutationFn: () =>
            applicationsService.startExport({
                search: debouncedSearch.trim() || undefined,
                cohortId: cohortFilter || undefined,
                assignmentStatus: assignmentFilter || undefined,
                paymentStatus: paymentFilter || undefined,
            }),
        onSuccess: (res) => {
            if (res.data?.jobId) {
                setExportJobId(res.data.jobId);
                setExportStatus('queued');
                setExportProgress(0);
                toast.loading('Export job queued in background...', { id: 'export-toast' });
            } else {
                toast.error('Failed to start export');
            }
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message ?? 'Failed to start export');
        },
    });

    useEffect(() => {
        if (!exportJobId) return;

        const pollInterval = setInterval(async () => {
            try {
                const res = await applicationsService.getExportStatus(exportJobId);
                const { state, progress, result } = res.data || {};
                
                if (state === 'completed' && result?.downloadUrl) {
                    clearInterval(pollInterval);
                    setExportJobId(null);
                    setExportStatus('idle');
                    setExportProgress(100);
                    toast.success('Excel export generated successfully!', { id: 'export-toast' });
                    
                    const downloadUrl = getFileUrl(result.downloadUrl);
                    
                    fetch(downloadUrl)
                        .then(response => response.blob())
                        .then(blob => {
                            const blobUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = blobUrl;
                            link.setAttribute('download', 'student_applications.xlsx');
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(blobUrl);
                        })
                        .catch(err => {
                            console.error('Blob download failed, falling back to direct download', err);
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.setAttribute('download', 'student_applications.xlsx');
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        });
                } else if (state === 'failed') {
                    clearInterval(pollInterval);
                    setExportJobId(null);
                    setExportStatus('idle');
                    toast.error('Excel export generation failed', { id: 'export-toast' });
                } else {
                    setExportStatus(state || 'active');
                    setExportProgress(progress || 0);
                    toast.loading(`Generating Excel export... ${progress || 0}%`, { id: 'export-toast' });
                }
            } catch (error) {
                clearInterval(pollInterval);
                setExportJobId(null);
                setExportStatus('idle');
                toast.error('Error fetching export status', { id: 'export-toast' });
            }
        }, 2000);

        return () => clearInterval(pollInterval);
    }, [exportJobId]);

    // Fetch mentors for dropdown selection
    const { data: mentorsResponse } = useQuery({
        queryKey: ['all-mentors-for-assign'],
        queryFn: () => mentorsService.getMentors(1, 100),
    });
    const mentorsList = useMemo(() => mentorsResponse?.data?.data ?? [], [mentorsResponse]);

    // Update selectedMentorId when selectedApp changes
    useEffect(() => {
        if (selectedApp) {
            setSelectedMentorId(selectedApp.enrollments?.[0]?.mentor_id || '');
        } else {
            setSelectedMentorId('');
        }
    }, [selectedApp]);

    const assignMentorMutation = useMutation({
        mutationFn: (mentorId: string) => {
            if (mentorId === 'deallocate') {
                return applicationsService.deallocateMentor(selectedApp.id);
            }
            return applicationsService.assignMentor(selectedApp.id, mentorId);
        },
        onSuccess: (_data, mentorId) => {
            queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
            queryClient.invalidateQueries({ queryKey: ['mentors'] });
            if (mentorId === 'deallocate') {
                toast.success('Mentor deallocated successfully');
                if (selectedApp) {
                    const updatedEnrollments = [...(selectedApp.enrollments || [])];
                    if (updatedEnrollments[0]) {
                        updatedEnrollments[0] = {
                            ...updatedEnrollments[0],
                            mentor_id: null,
                            mentor: null,
                        };
                    }
                    setSelectedApp({
                        ...selectedApp,
                        enrollments: updatedEnrollments,
                    });
                    setSelectedMentorId('');
                }
                return;
            }
            toast.success('Mentor assigned successfully');
            if (selectedApp) {
                const updatedEnrollments = [...(selectedApp.enrollments || [])];
                const mentorObj = mentorsList.find((m: any) => m.id === selectedMentorId);
                if (updatedEnrollments[0]) {
                    updatedEnrollments[0] = {
                        ...updatedEnrollments[0],
                        mentor_id: selectedMentorId,
                        mentor: mentorObj || null,
                    };
                } else {
                    updatedEnrollments[0] = {
                        mentor_id: selectedMentorId,
                        mentor: mentorObj || null,
                    };
                }
                setSelectedApp({
                    ...selectedApp,
                    enrollments: updatedEnrollments,
                });
            }
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message ?? 'Failed to update mentor assignment');
        },
    });

    const deallocateMentorMutation = useMutation({
        mutationFn: () => applicationsService.deallocateMentor(selectedApp.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-applications'] });
            queryClient.invalidateQueries({ queryKey: ['mentors'] });
            toast.success('Mentor deallocated successfully');
            if (selectedApp) {
                const updatedEnrollments = [...(selectedApp.enrollments || [])];
                if (updatedEnrollments[0]) {
                    updatedEnrollments[0] = {
                        ...updatedEnrollments[0],
                        mentor_id: null,
                        mentor: null,
                    };
                }
                setSelectedApp({
                    ...selectedApp,
                    enrollments: updatedEnrollments,
                });
                setSelectedMentorId('');
            }
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message ?? 'Failed to deallocate mentor');
        },
    });

    const resendPaymentLinkMutation = useMutation({
        mutationFn: (id: string) => applicationsService.resendPaymentLink(id),
        onSuccess: (res) => {
            toast.success(res.message || 'Payment link resent successfully');
        },
        onError: (err: any) => {
            toast.error(err?.response?.data?.message ?? 'Failed to resend payment link');
        },
    });

    const handleAutoSelectMentor = () => {
        if (mentorsList.length === 0) {
            toast.error('No mentors available to select');
            return;
        }
        const sortedMentors = [...mentorsList].sort((a: any, b: any) => {
            const countA = a._count?.enrollments ?? 0;
            const countB = b._count?.enrollments ?? 0;
            return countA - countB;
        });
        const leastLoaded = sortedMentors[0];
        if (leastLoaded) {
            setSelectedMentorId(leastLoaded.id);
            toast.success(`Selected least loaded mentor: ${leastLoaded.name} (${leastLoaded._count?.enrollments ?? 0} students)`);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const listQueryKey = ['admin-applications', pageIndex, PAGE_SIZE, debouncedSearch, cohortFilter, assignmentFilter, paymentFilter];

    const { data: listResponse, isLoading, isError } = useQuery({
        queryKey: listQueryKey,
        queryFn: () =>
            applicationsService.adminList(
                pageIndex + 1,
                PAGE_SIZE,
                debouncedSearch.trim() || undefined,
                cohortFilter || undefined,
                assignmentFilter || undefined,
                paymentFilter || undefined,
            ),
        placeholderData: previousData => previousData,
        staleTime: 2 * 60 * 1000,
    });

    // When navigating to a new page via Prev/Next and selectedApp is null but
    // we have a pending index (selectedAppIndex >= 0), auto-open that record.
    useEffect(() => {
        const freshData = listResponse?.data?.data ?? [];
        if (selectedApp === null && selectedAppIndex >= 0 && freshData.length > 0) {
            // Clamp in case previous page had fewer items
            const clampedIndex = Math.min(selectedAppIndex, freshData.length - 1);
            setSelectedApp(freshData[clampedIndex]);
            setSelectedAppIndex(clampedIndex);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listResponse]);

    const { data: cohortsResponse } = useQuery({
        queryKey: ['all-cohorts-for-filter'],
        queryFn: () => cohortsService.getCohorts(1, 100),
    });

    const cohortOptions = useMemo(() => {
        const list = cohortsResponse?.data?.data ?? [];
        return [
            { value: '', label: 'All Cohorts' },
            ...list.map(c => ({ value: c.id, label: c.generated_cohort_title })),
        ];
    }, [cohortsResponse]);

    const assignmentOptions = [
        { value: '', label: 'All Assignments' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'ASSIGNED_TO_COHORT', label: 'Assigned' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
    ];

    const paymentOptions = [
        { value: '', label: 'All Payments' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'PAID', label: 'Paid' },
    ];

    const columns = useMemo<ColumnDef<any>[]>(() => [
        {
            accessorKey: 'full_name',
            header: 'Student Name',
            size: 200,
            cell: ({ row }) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-[var(--secondary-color)]">
                        {row.original.full_name}
                    </span>
                    <span className="text-xs text-[var(--disabled-text-color)]">
                        {row.original.applicant_type === 'parent' ? 'Parent Applied' : 'Student Applied'}
                    </span>
                </div>
            ),
        },
        {
            accessorKey: 'email',
            header: 'Email',
            size: 180,
            cell: ({ row }) => (
                <span className="text-sm text-[var(--secondary-color)]">{row.original.email}</span>
            ),
        },
        {
            accessorKey: 'applied_cohort',
            header: 'Applied / Assigned Cohort',
            size: 240,
            cell: ({ row }) => {
                const applied = row.original.applied_cohort?.generated_cohort_title;
                const assigned = row.original.assigned_cohort?.generated_cohort_title;
                return (
                    <div className="flex flex-col text-xs gap-0.5">
                        <div>
                            <span className="font-medium text-[var(--disabled-text-color)]">Applied: </span>
                            {applied || 'N/A'}
                        </div>
                        {assigned && (
                            <div>
                                <span className="font-medium text-emerald-600">Assigned: </span>
                                <span className="font-semibold text-emerald-700">{assigned}</span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'enrollment_number',
            header: 'Enrollment No.',
            size: 140,
            cell: ({ row }) => (
                <span className="text-sm font-mono font-medium text-[var(--secondary-color)]">
                    {row.original.enrollment_number || 'N/A'}
                </span>
            ),
        },
        {
            accessorKey: 'status',
            header: 'Action / Assignment',
            size: 130,
            cell: ({ row }) => {
                const status = row.original.status;
                let badgeClass = 'bg-amber-50 text-amber-700 border-amber-200';
                let label = 'Pending';
                if (status === 'ASSIGNED_TO_COHORT') {
                    badgeClass = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                    label = 'Assigned';
                } else if (status === 'APPROVED') {
                    badgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
                    label = 'Approved';
                } else if (status === 'REJECTED') {
                    badgeClass = 'bg-rose-50 text-rose-700 border-rose-200';
                    label = 'Rejected';
                }
                return (
                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${badgeClass}`}>
                        {label}
                    </span>
                );
            },
        },
        {
            accessorKey: 'payment_status',
            header: 'Payment Status',
            size: 130,
            cell: ({ row }) => {
                const status = row.original.payment_status;
                const badgeClass = status === 'PAID'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200';
                return (
                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${badgeClass}`}>
                        {status || 'PENDING'}
                    </span>
                );
            },
        },
        {
            accessorKey: 'created_at',
            header: 'Application Date',
            size: 140,
            cell: ({ row }) => {
                const date = new Date(row.original.created_at);
                return (
                    <span className="text-sm text-[var(--secondary-color)]">
                        {date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                );
            },
        },
        {
            accessorKey: 'referral_code',
            header: 'Referral Code',
            size: 180,
            cell: ({ row }) => (
                <span className="text-sm text-[var(--secondary-color)]">{row.original.referral_code}</span>
            ),
        },
        {
            id: 'actions',
            header: 'Action',
            size: 100,
            cell: ({ row }) => {
                const actions = [
                    {
                        icon: 'eyeShowPrimary' as const,
                        label: 'View Details',
                        onClick: () => {
                            setSelectedApp(row.original);
                            setSelectedAppIndex(row.index);
                        },
                    },
                    {
                        icon: 'delete' as const,
                        label: 'Delete Application',
                        onClick: () => setPendingDeleteId(row.original.id),
                        variant: 'danger' as const,
                    },
                ];
                return <TableRowIconActions actions={actions} />;
            },
        },
    ], [setSelectedApp]);

    const searchIcon = (
        <button className="flex items-center cursor-pointer">
            <Icon name="search" className="w-3 2xl:w-[14px] h-auto" />
        </button>
    );

    const currentPageData = listResponse?.data?.data ?? [];
    const totalCount = listResponse?.data?.total ?? 0;
    const globalIndex = pageIndex * PAGE_SIZE + selectedAppIndex;

    const handlePrev = () => {
        if (selectedAppIndex > 0) {
            // Navigate within current page
            const prevApp = currentPageData[selectedAppIndex - 1];
            setSelectedApp(prevApp);
            setSelectedAppIndex(selectedAppIndex - 1);
        } else if (pageIndex > 0) {
            // Go to previous page and select its last item
            const prevPage = pageIndex - 1;
            setPageIndex(prevPage);
            // selectedAppIndex will be updated after data loads via useEffect below
            setSelectedAppIndex(PAGE_SIZE - 1); // will be clamped after fetch
            setSelectedApp(null); // clear until new page loads
        }
    };

    const handleNext = () => {
        if (selectedAppIndex < currentPageData.length - 1) {
            // Navigate within current page
            const nextApp = currentPageData[selectedAppIndex + 1];
            setSelectedApp(nextApp);
            setSelectedAppIndex(selectedAppIndex + 1);
        } else if (globalIndex < totalCount - 1) {
            // Go to next page and select its first item
            const nextPage = pageIndex + 1;
            setPageIndex(nextPage);
            setSelectedAppIndex(0);
            setSelectedApp(null); // clear until new page loads
        }
    };

    const isPrevDisabled = globalIndex <= 0;
    const isNextDisabled = globalIndex >= totalCount - 1;

    return (
        <TableRowActionsTooltipProvider>
            <PageHeader
                title="Student Applications"
                breadcrumbItems={[
                    { label: 'Dashboard', to: '/admin/dashboard' },
                    { label: 'Applications' },
                ]}
                actions={
                    <Button
                        onClick={() => startExportMutation.mutate()}
                        isLoading={startExportMutation.isPending || exportStatus !== 'idle'}
                        variant="dark"
                        size="sm"
                        className="flex items-center gap-1.5"
                    >
                        <Icon name="export" size={16} className="brightness-0 invert" />
                        {exportStatus !== 'idle' ? `Exporting (${exportProgress}%)` : 'Export Excel'}
                    </Button>
                }
            />
            <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 lg:gap-4 p-3 bg-white rounded-2xl border border-[var(--neutral-color)]">
                    <div className="md:col-span-2">
                        <Input
                            type="text"
                            placeholder="Search by student name, email or enrollment no..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            leftElement={searchIcon}
                        />
                    </div>
                    <div>
                        <Select
                            placeholder="Filter by Cohort"
                            value={cohortFilter}
                            onChange={setCohortFilter}
                            options={cohortOptions}
                        />
                    </div>
                    <div>
                        <Select
                            placeholder="Assignment Status"
                            value={assignmentFilter}
                            onChange={setAssignmentFilter}
                            options={assignmentOptions}
                        />
                    </div>
                    <div>
                        <Select
                            placeholder="Payment Status"
                            value={paymentFilter}
                            onChange={setPaymentFilter}
                            options={paymentOptions}
                        />
                    </div>
                </div>

                {isError ? (
                    <div className="bg-white rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                        Failed to load student applications.
                    </div>
                ) : (
                    <DataGridTable
                        columns={columns}
                        data={currentPageData}
                        loading={isLoading}
                        totalCount={totalCount}
                        pageIndex={pageIndex}
                        pageSize={PAGE_SIZE}
                        onPageChange={(p) => { setPageIndex(p); setSelectedApp(null); }}
                    />
                )}
            </div>

            {/* View Details Modal */}
            <Modal
                isOpen={selectedApp !== null}
                onClose={() => setSelectedApp(null)}
                title="Application Details"
                maxWidth="max-w-[800px]"
                onPrev={handlePrev}
                onNext={handleNext}
                isPrevDisabled={isPrevDisabled}
                isNextDisabled={isNextDisabled}
            >
                {selectedApp && (
                    <>
                        <div className="overflow-y-auto p-4 2xl:p-5 space-y-6 max-h-[65vh] flex-1 text-sm text-[var(--secondary-color)]">
                            {/* Premium Header Banner Card */}
                            <div className="relative overflow-hidden bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-2xl p-5 shadow-lg border border-slate-800 shrink-0">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl transform translate-x-8 -translate-y-8 pointer-events-none"></div>
                                <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        {(() => {
                                            const initials = selectedApp.full_name
                                                ?.split(' ')
                                                ?.map((n: string) => n[0])
                                                ?.join('')
                                                ?.toUpperCase()
                                                ?.slice(0, 2) || 'SA';
                                            return (
                                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-blue-500 to-indigo-500 flex items-center justify-center font-bold text-xl text-white shadow-inner shrink-0 border border-indigo-400/30">
                                                    {initials}
                                                </div>
                                            );
                                        })()}
                                        <div>
                                            <h3 className="font-bold text-lg leading-tight tracking-tight">{selectedApp.full_name}</h3>
                                            <p className="text-xs text-slate-300 font-medium mt-1">
                                                {selectedApp.applicant_type === 'parent' ? '👨‍👦 Parent Applied' : '🎓 Student Applied'} • Applied {new Date(selectedApp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wide font-semibold border ${
                                            selectedApp.status === 'ASSIGNED_TO_COHORT' 
                                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                                : selectedApp.status === 'APPROVED'
                                                ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                                                : selectedApp.status === 'REJECTED'
                                                ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                                                : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                        }`}>
                                            {selectedApp.status === 'ASSIGNED_TO_COHORT' ? 'Assigned' : selectedApp.status || 'Pending'}
                                        </span>
                                        <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wide font-semibold border ${
                                            selectedApp.payment_status === 'PAID'
                                                ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                                : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                        }`}>
                                            Payment: {selectedApp.payment_status || 'PENDING'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Section: 2-Column Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Card 1: Personal & Contact Information */}
                                <div className="bg-slate-50/50 hover:bg-slate-50 transition-colors p-5 rounded-2xl border border-slate-100 flex flex-col gap-3.5 shadow-sm">
                                    <h4 className="font-bold text-xs uppercase tracking-wider text-[var(--disabled-text-color)] mb-1 flex items-center gap-1.5 border-b pb-2">
                                        <span className="w-1.5 h-3 bg-blue-500 rounded-sm"></span> Personal & Contact
                                    </h4>
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-3.5 text-xs">
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Email Address</span>
                                            <span className="font-semibold text-slate-800 break-all">{selectedApp.email}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Contact Number</span>
                                            <span className="font-semibold text-slate-800">{selectedApp.contact_number}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Parent Name</span>
                                            <span className="font-semibold text-slate-800">{selectedApp.parent_name || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Location</span>
                                            <span className="font-semibold text-slate-800">
                                                {selectedApp.city ? `${selectedApp.city}, ` : ''}{selectedApp.state || ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Card 2: Academic Profile & Experience */}
                                <div className="bg-slate-50/50 hover:bg-slate-50 transition-colors p-5 rounded-2xl border border-slate-100 flex flex-col gap-3.5 shadow-sm">
                                    <h4 className="font-bold text-xs uppercase tracking-wider text-[var(--disabled-text-color)] mb-1 flex items-center gap-1.5 border-b pb-2">
                                        <span className="w-1.5 h-3 bg-indigo-500 rounded-sm"></span> Academic & Coding Profile
                                    </h4>
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-3.5 text-xs">
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">School Name</span>
                                            <span className="font-semibold text-slate-800 block line-clamp-1">{selectedApp.school_name}</span>
                                            <span className="text-[10px] text-slate-400 font-medium uppercase">{selectedApp.school_type}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Grade / Class</span>
                                            <span className="font-semibold text-slate-800">Grade {selectedApp.grade}</span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-1">Programming Experience</span>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 bg-slate-200 h-2 rounded-full overflow-hidden">
                                                    <div 
                                                        className="bg-indigo-500 h-full rounded-full transition-all duration-500" 
                                                        style={{ width: `${(selectedApp.programming_experience / 5) * 100}%` }}
                                                    ></div>
                                                </div>
                                                <span className="font-bold text-slate-800 shrink-0 text-xs">{selectedApp.programming_experience} / 5</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section: Accomplishments (Full Width) */}
                            <div className="bg-slate-50/50 hover:bg-slate-50 transition-colors p-5 rounded-2xl border border-slate-100 flex flex-col gap-3 shadow-sm">
                                <h4 className="font-bold text-xs uppercase tracking-wider text-[var(--disabled-text-color)] mb-1 flex items-center gap-1.5 border-b pb-2">
                                    <span className="w-1.5 h-3 bg-amber-500 rounded-sm"></span> Statement / Key Accomplishments
                                </h4>
                                <p className="text-xs leading-relaxed text-slate-700 bg-white border border-slate-100 p-4 rounded-xl shadow-inner whitespace-pre-line font-medium italic relative">
                                    <span className="absolute top-2 right-3 text-4xl text-slate-100 pointer-events-none font-serif leading-none">“</span>
                                    {selectedApp.accomplishments || 'No accomplishments listed.'}
                                </p>
                            </div>

                            {/* Section: Program Assignment & Documents */}
                            <div className="bg-slate-50/50 hover:bg-slate-50 transition-colors p-5 rounded-2xl border border-slate-100 flex flex-col gap-4 shadow-sm">
                                <h4 className="font-bold text-xs uppercase tracking-wider text-[var(--disabled-text-color)] mb-1 flex items-center gap-1.5 border-b pb-2">
                                    <span className="w-1.5 h-3 bg-emerald-500 rounded-sm"></span> Enrollment & Registration Letters
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                                    <div className="flex flex-col gap-3">
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Applied Cohort</span>
                                            <span className="font-semibold text-slate-800">{selectedApp.applied_cohort?.generated_cohort_title || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-0.5">Assigned Cohort</span>
                                            <span className={`font-bold ${selectedApp.assigned_cohort?.generated_cohort_title ? 'text-emerald-600' : 'text-slate-500'}`}>
                                                {selectedApp.assigned_cohort?.generated_cohort_title || 'Not Assigned Yet'}
                                            </span>
                                        </div>
                                        {selectedApp.status === 'ASSIGNED_TO_COHORT' && selectedApp.payment_status === 'PENDING' && (
                                            <div className="mt-1">
                                                <button
                                                    type="button"
                                                    disabled={resendPaymentLinkMutation.isPending}
                                                    onClick={() => resendPaymentLinkMutation.mutate(selectedApp.id)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-xl text-[11px] font-semibold border border-indigo-200 transition-colors shadow-sm cursor-pointer disabled:opacity-50"
                                                >
                                                    {resendPaymentLinkMutation.isPending ? 'Resending Link...' : '✉️ Resend Welcome / Payment Link'}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <div>
                                            <span className="block text-[var(--disabled-text-color)] font-medium mb-1.5">Letter of Registration (LOA)</span>
                                            {selectedApp.lor_path ? (
                                                <a
                                                    href={getFileUrl(selectedApp.lor_path)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl text-xs font-semibold border border-emerald-200 transition-colors shadow-sm w-fit"
                                                >
                                                    <Icon name="download" size={14} /> Download LOA
                                                </a>
                                            ) : (
                                                <span className="text-[var(--disabled-text-color)] italic">Pending Payment</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Manual Mentor Assignment Dropdown Section */}
                                {selectedApp.enrollments?.[0] ? (
                                    <div className="border-t pt-4 mt-2">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-xs text-slate-700 font-semibold">
                                                1-on-1 Mentor Assignment
                                            </span>
                                            {mentorsList.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={handleAutoSelectMentor}
                                                    className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1 transition-colors animate-pulse"
                                                >
                                                    ⚡ Auto-Select (Least Loaded)
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                                            <div className="flex-1">
                                                <label className="text-[11px] text-[var(--disabled-text-color)] font-medium block mb-1">
                                                    {selectedApp.enrollments?.[0]?.mentor ? (
                                                        <span>
                                                            Currently Assigned: <strong className="text-emerald-700">{selectedApp.enrollments[0].mentor.name}</strong> ({selectedApp.enrollments[0].mentor.speciality})
                                                        </span>
                                                    ) : (
                                                        <span className="text-amber-600 font-semibold">⚠️ No Mentor Assigned</span>
                                                    )}
                                                </label>
                                                <select
                                                    className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white text-xs font-semibold focus:outline-none focus:border-indigo-500 transition-colors"
                                                    value={selectedMentorId}
                                                    onChange={(e) => setSelectedMentorId(e.target.value)}
                                                >
                                                    <option value="">-- Select Mentor to Assign --</option>
                                                    {selectedApp.enrollments?.[0]?.mentor && (
                                                        <option value="deallocate" className="text-rose-600 font-bold">
                                                            🚫 Remove / Deallocate Assigned Mentor
                                                        </option>
                                                    )}
                                                    {mentorsList.map((m: any) => {
                                                        const count = m._count?.enrollments ?? 0;
                                                        return (
                                                            <option key={m.id} value={m.id}>
                                                                {m.name} ({m.speciality}) — {count} {count === 1 ? 'student' : 'students'}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <Button
                                                    onClick={() => assignMentorMutation.mutate(selectedMentorId)}
                                                    disabled={!selectedMentorId || assignMentorMutation.isPending || deallocateMentorMutation.isPending}
                                                    className="px-4 py-2 text-xs shrink-0 rounded-xl h-[34px]"
                                                >
                                                    {assignMentorMutation.isPending
                                                        ? 'Saving...'
                                                        : selectedMentorId === 'deallocate'
                                                        ? 'Remove Mentor'
                                                        : selectedApp.enrollments?.[0]?.mentor
                                                        ? 'Change Mentor'
                                                        : 'Assign Mentor'}
                                                </Button>
                                                {selectedApp.enrollments?.[0]?.mentor && (
                                                    <Button
                                                        variant="dark"
                                                        onClick={() => deallocateMentorMutation.mutate()}
                                                        disabled={deallocateMentorMutation.isPending || assignMentorMutation.isPending}
                                                        className="px-3.5 py-2 text-xs shrink-0 rounded-xl h-[34px] !bg-rose-50 !text-rose-600 hover:!bg-rose-100 !border !border-rose-200"
                                                    >
                                                        {deallocateMentorMutation.isPending ? 'Deallocating...' : 'Deallocate'}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="border-t pt-4 mt-2">
                                        <span className="block text-xs text-slate-700 font-semibold mb-2">
                                            1-on-1 Mentor Assignment
                                        </span>
                                        <span className="text-xs text-amber-600 font-medium bg-amber-50 border border-amber-100 p-3 rounded-xl block">
                                            ⚠️ Assign the student to a cohort first to enable 1-on-1 mentor assignment.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between border-t border-[var(--neutral-color)] px-4 2xl:px-5 py-2 2xl:py-3 shrink-0">
                            {/* Navigation: Prev / Counter / Next */}
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handlePrev}
                                    disabled={isPrevDisabled}
                                    title="Previous application"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <span className="text-xs font-semibold text-slate-500 min-w-[60px] text-center tabular-nums">
                                    {globalIndex + 1} <span className="font-normal text-slate-400">/</span> {totalCount}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    disabled={isNextDisabled}
                                    title="Next application"
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                            <Button
                                type="button"
                                variant="dark"
                                onClick={() => setSelectedApp(null)}
                            >
                                Close
                            </Button>
                        </div>
                    </>
                )}
            </Modal>

            <ConfirmModal
                isOpen={pendingDeleteId !== null}
                onClose={() => setPendingDeleteId(null)}
                onConfirm={() => {
                    if (pendingDeleteId !== null) {
                        deleteMutation.mutate(pendingDeleteId);
                        setPendingDeleteId(null);
                    }
                }}
                loading={deleteMutation.isPending}
                title="Delete Application"
                message="Are you sure you want to permanently delete this student application? This action cannot be undone."
                confirmLabel="Delete"
                variant="danger"
            />
        </TableRowActionsTooltipProvider>
    );
};

export default StudentApplicationsPage;
